import React, { useMemo } from 'react';
import { Button, Alert, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FiSave, FiX, FiEye, FiEdit, FiTrash2 } from 'react-icons/fi';
import ReactDatePicker from 'react-datepicker';
import { format, startOfDay } from 'date-fns';
import { formatDate, parseUserInfo } from '../../../utilities/common';
import { getActivityTypeOptions } from '../../model-scenarios/scenarioConfig';

const COLUMNS = [
  { key: 'invoiceNumber', label: 'Invoice Number', type: 'text', minWidth: '120px' },
  { key: 'provider', label: 'Provider', type: 'text', minWidth: '120px' },
  { key: 'currency', label: 'Currency', type: 'text', minWidth: '80px' },
  { key: 'meterNumber', label: 'Meter Number', type: 'text', minWidth: '120px' },
  { key: 'utility', label: 'Utility', type: 'text', minWidth: '100px' },
  { key: 'activityType', label: 'Activity Type', type: 'select', minWidth: '160px' },
  { key: 'periodStart', label: 'Period Start Date', type: 'date', minWidth: '140px', required: true },
  { key: 'periodEnd', label: 'Period End Date', type: 'date', minWidth: '140px', required: true },
  { key: 'contribution', label: 'Contribution', type: 'number', minWidth: '120px', required: true },
  { key: 'contributionUnit', label: 'Unit', type: 'text', minWidth: '100px', required: true },
  { key: 'cost', label: 'Cost', type: 'number', minWidth: '100px' }
];

const toTitleCase = (value) => {
  if (!value) return '—';
  return value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const displayValue = (col, meter, labelMap) => {
  if (col.key === 'periodStart' || col.key === 'periodEnd') {
    return formatDate(meter[col.key], '—');
  }
  if (col.type === 'select') {
    const raw = meter[col.key];
    return labelMap[raw] || toTitleCase(raw) || '—';
  }
  return meter[col.key] ?? '—';
};

const MeterRow = ({
  meter,
  meterKey,
  groupIndex,
  meterIndex,
  isEditing,
  displayMeter,
  activityGroups,
  activityLabelMap,
  usrDateFormat,
  onEdit,
  onSaveMeterEdit,
  onCancelEdit,
  onDeleteClick,
  onFieldChange
}) => {
  const { t } = useTranslation();

  return (
    <tr style={{ verticalAlign: 'middle' }}>
      {COLUMNS.map((col) => (
        <td key={col.key}>
          {isEditing ? (
            col.type === 'select' ? (
              <Form.Control
                as="select"
                size="sm"
                value={displayMeter[col.key] || ''}
                onChange={(e) => onFieldChange(meterKey, col.key, e.target.value)}
              >
                <option value="">—</option>
                {displayMeter[col.key] && !activityLabelMap[displayMeter[col.key]] && (
                  <option value={displayMeter[col.key]}>{toTitleCase(displayMeter[col.key])}</option>
                )}
                {activityGroups.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Form.Control>
            ) : col.type === 'date' ? (
              <ReactDatePicker
                dateFormat={usrDateFormat}
                selected={displayMeter[col.key] ? new Date(displayMeter[col.key]) : null}
                onChange={(dt) => {
                  const val = dt ? format(startOfDay(dt), 'yyyy-MM-dd') : '';
                  onFieldChange(meterKey, col.key, val);
                }}
                className="form-control form-control-sm"
                peekNextMonth
                showMonthDropdown
                showYearDropdown
              />
            ) : (
              <Form.Control
                size="sm"
                type={col.type}
                value={displayMeter[col.key] || ''}
                onChange={(e) => onFieldChange(meterKey, col.key, e.target.value)}
              />
            )
          ) : (
            displayValue(col, displayMeter, activityLabelMap)
          )}
        </td>
      ))}
      <td>
        {isEditing ? (
          <div className="d-flex">
            <Button
              variant="link"
              size="sm"
              className="p-0 mr-2"
              onClick={() => onSaveMeterEdit(groupIndex, meterIndex)}
              title={t('Save')}
            >
              <FiSave size={18} className="text-success" />
            </Button>
            <Button variant="link" size="sm" className="p-0" onClick={() => onCancelEdit(meterKey)} title={t('Cancel')}>
              <FiX size={18} className="text-danger" />
            </Button>
          </div>
        ) : (
          <div className="d-flex">
            {displayMeter.viewUrl && (
              <Button
                variant="link"
                size="sm"
                className="p-0 mr-2"
                onClick={() => window.open(displayMeter.viewUrl, '_blank')}
                title={t('View Bill')}
              >
                <FiEye size={18} />
              </Button>
            )}
            <Button
              variant="link"
              size="sm"
              className="p-0 mr-2"
              onClick={() => onEdit(groupIndex, meterIndex, meter)}
              title={t('Edit')}
            >
              <FiEdit size={18} />
            </Button>
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={() => onDeleteClick(groupIndex, meterIndex)}
              title={t('Delete')}
            >
              <FiTrash2 size={18} className="text-danger" />
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
};

const MeterTable = ({
  meters,
  groupIndex,
  editingMeters,
  onEdit,
  onSaveMeterEdit,
  onCancelEdit,
  onDeleteClick,
  onFieldChange
}) => {
  const { t } = useTranslation();
  const { usrDateFormat } = parseUserInfo(useSelector((state) => state.users.user));
  const activityGroups = useMemo(() => getActivityTypeOptions(t), [t]);
  const activityLabelMap = useMemo(
    () =>
      activityGroups.reduce((map, g) => {
        g.options.forEach((o) => {
          map[o.value] = o.label;
        });
        return map;
      }, {}),
    [activityGroups]
  );

  if (!meters || meters.length === 0) {
    return <Alert variant="warning">{t('No meters found')}</Alert>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-bordered" style={{ fontSize: '14px' }}>
        <thead className="thead-light">
          <tr style={{ whiteSpace: 'nowrap' }}>
            {COLUMNS.map((col) => (
              <th key={col.key} style={{ minWidth: col.minWidth }}>
                {t(col.label)}
                {col.required && <span className="text-danger">*</span>}
              </th>
            ))}
            <th style={{ minWidth: '100px' }}>{t('Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {meters.map((meter, meterIndex) => {
            const meterKey = `${groupIndex}-${meterIndex}`;
            const isEditing = meterKey in editingMeters;
            const displayMeter = isEditing ? editingMeters[meterKey] : meter;

            return (
              <MeterRow
                key={meterIndex}
                meter={meter}
                meterKey={meterKey}
                groupIndex={groupIndex}
                meterIndex={meterIndex}
                isEditing={isEditing}
                displayMeter={displayMeter}
                activityGroups={activityGroups}
                activityLabelMap={activityLabelMap}
                usrDateFormat={usrDateFormat}
                onEdit={onEdit}
                onSaveMeterEdit={onSaveMeterEdit}
                onCancelEdit={onCancelEdit}
                onDeleteClick={onDeleteClick}
                onFieldChange={onFieldChange}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default MeterTable;
